import React, { useEffect, useState, useCallback, useRef } from "react";
import InitialsAvatar from "react-avatar";
import {
    collection,
    getDocs,
    onSnapshot,
    orderBy,
    query,
    limit,
    startAfter,
    DocumentData,
    QueryDocumentSnapshot,
    where,
} from "firebase/firestore";
import InfiniteScroll from "react-infinite-scroll-component";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Container } from "reactstrap";
import SimpleBar from "simplebar-react";
import { ChatRoom, EntityChatRoom } from "types/chat";
import { useFirebase } from "contexts/FirebaseAuthContext";
import { useSelector } from "react-redux";
import { RootState } from "store";
import DOMPurify from "dompurify";
import useDocumentTitle from "hooks/useDocumentTitle";
import "./style.scss";

const PAGE_SIZE = 20;

const ChatRoomListItem: React.FC<{ chatRoom: EntityChatRoom; active: boolean }> = ({ chatRoom, active }) => {
    const { auth } = useFirebase();
    const lastMessage = chatRoom.lastMessage;
    const currentUserId = auth.currentUser?.uid;
    const lastMessageText = DOMPurify.sanitize(
        (lastMessage?.text?.split("&nbsp;")?.[0] ?? "") || "No messages yet",
        { ALLOWED_TAGS: [], ALLOWED_ATTR: [] }
    );
    const lastSender =
        currentUserId === lastMessage?.senderId ? "You" : lastMessage?.senderName || "";

    return (
        <li className={active ? "active" : ""}>
            <Link to={`/chat/${chatRoom.uuid}`}>
                <div className="d-flex align-items-center">
                    <div className="flex-shrink-0 chat-user-img align-self-center me-2 ms-0">
                        {chatRoom.social_account?.profile_picture_url ? (
                            <img
                                src={chatRoom.social_account.profile_picture_url}
                                className="rounded-circle avatar-xs"
                                alt=""
                            />
                        ) : (
                            <InitialsAvatar
                                name={chatRoom.influencer?.name ?? chatRoom.social_account.name ?? chatRoom?.social_account?.handle ?? ""}
                                maxInitials={2}
                                size="32"
                                round
                                className="rounded-circle avatar-xs"
                            />
                        )}
                    </div>
                    <div className="flex-grow-1 overflow-hidden">
                        <p className={`text-truncate mb-0 ${(chatRoom.unreadCount ?? 0) > 0 ? "unread-msg-user" : ""}`}>
                            {chatRoom.influencer?.name ?? chatRoom.social_account.name ?? chatRoom?.social_account?.handle ?? ""}
                        </p>
                        {lastMessage && (
                            <span className="text-truncate fs-12 text-muted d-block chat-user-message">
                                {lastSender && <span className="fw-bold">{lastSender}: </span>}
                                <span>{lastMessageText}</span>
                            </span>
                        )}
                        <p className="text-truncate text-muted fs-12 mb-0">{chatRoom.campaign.name}</p>
                    </div>
                    {(chatRoom.unreadCount ?? 0) > 0 && (
                        <div className="unread-message">
                            <span className="badge bg-success">{chatRoom.unreadCount}</span>
                        </div>
                    )}
                </div>
            </Link>
        </li>
    );
};

const ChatLayout: React.FC<{
    children: React.ReactNode;
    setEntityChatRoom?: (room: EntityChatRoom | null) => void;
    setListLoaded?: (loaded: boolean) => void;
}> = ({ children, setEntityChatRoom, setListLoaded }) => {
    useDocumentTitle("Chats");
    const { uuid: activeRoomId } = useParams();
    const [chatRooms, setChatRooms] = useState<EntityChatRoom[]>([]);
    const [hasMore, setHasMore] = useState(true);
    const { db, firebaseUser } = useFirebase();
    const hubId = useSelector((state: RootState) => state.auth.selectedHubId);
    const navigate = useNavigate();
    const lastVisibleRef = useRef<QueryDocumentSnapshot<DocumentData> | null>(null);
    const chatViewRef = useRef<HTMLDivElement | null>(null);
    const simpleBarRef = useRef<any>(null);
    const [scrollContainerId, setScrollContainerId] = useState("");

    useEffect(() => {
        if (simpleBarRef.current) {
            const scrollEl = simpleBarRef.current.getScrollElement();
            scrollEl.id = "chat-simplebar-scrollable-div";
            setScrollContainerId("chat-simplebar-scrollable-div");
        }
    }, []);

    useEffect(() => {
        if (activeRoomId) {
            if (window.innerWidth < 892 && chatViewRef.current) {
                chatViewRef.current.classList.add("user-chat-show");
            }
        } else if (chatViewRef.current) {
            if (window.innerWidth < 892) {
                chatViewRef.current.classList.remove("user-chat-show");
            }
        }
    }, [activeRoomId]);

    useEffect(() => {
        if (!activeRoomId && Array.isArray(chatRooms) && chatRooms.length > 0 && window.innerWidth >= 768) {
            navigate(`/chat/${chatRooms[0].uuid}`);
        }
    }, [activeRoomId, chatRooms, navigate]);

    useEffect(() => {
        if (!hubId || !firebaseUser) return;

        const q = query(
            collection(db, "entityChatRooms", hubId, "rooms"),
            where("status", "==", true),
            orderBy("lastMessage.timestamp", "desc"),
            limit(PAGE_SIZE)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            if (!snapshot.empty) {
                const docs = snapshot.docs.map((d) => ({
                    ...(d.data() as EntityChatRoom),
                    uuid: d.id,
                }));
                setChatRooms([...docs]);
                lastVisibleRef.current = snapshot.docs[snapshot.docs.length - 1];
                setHasMore(snapshot.docs.length === PAGE_SIZE);
            } else {
                setChatRooms([]);
                setHasMore(false);
            }
            setListLoaded?.(true);
        });

        return () => unsubscribe();
    }, [hubId, firebaseUser, db, setListLoaded]);

    const fetchNextPage = useCallback(async () => {
        const lastVisible = lastVisibleRef.current;
        if (!lastVisible || !db || !hubId) return;

        const q = query(
            collection(db, "entityChatRooms", hubId, "rooms"),
            where("status", "==", true),
            orderBy("lastMessage.timestamp", "desc"),
            startAfter(lastVisible),
            limit(PAGE_SIZE)
        );

        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
            const docs = snapshot.docs.map((d) => ({
                ...(d.data() as ChatRoom),
                uuid: d.id,
            }));
            setChatRooms((prev) => [...prev, ...docs]);
            lastVisibleRef.current = snapshot.docs[snapshot.docs.length - 1];
            setHasMore(snapshot.docs.length === PAGE_SIZE);
        } else {
            setHasMore(false);
        }
    }, [db, hubId]);

    useEffect(() => {
        if (activeRoomId && chatRooms && setEntityChatRoom) {
            const activeChatRoom = chatRooms.find((room) => room.uuid === activeRoomId);
            setEntityChatRoom(activeChatRoom ?? null);
        } else {
            setEntityChatRoom?.(null);
        }
    }, [activeRoomId, chatRooms, setEntityChatRoom]);

    return (
        <div className="page-content chat-page">
            <Container fluid>
                <div className="chat-wrapper d-lg-flex gap-1 mx-n4 mt-n4 p-1">
                    <div className="chat-leftsidebar">
                        <div className="p-3 pb-4">
                            <div className="d-flex align-items-center gap-2">
                                <h5 className="m-0">Chats</h5>
                            </div>
                        </div>
                        <SimpleBar
                            className="chat-room-list pt-3"
                            ref={simpleBarRef}
                            style={{ margin: "-16px 0px 0px" }}
                        >
                            {scrollContainerId && (
                                <InfiniteScroll
                                    dataLength={chatRooms.length}
                                    next={fetchNextPage}
                                    hasMore={hasMore}
                                    loader={<></>}
                                    className="list"
                                    scrollableTarget={scrollContainerId}
                                >
                                    <div className="chat-message-list">
                                        <ul className="list-unstyled chat-list chat-user-list users-list" id="chatRoomList">
                                            {chatRooms.map((chatRoom, i) => (
                                                <ChatRoomListItem
                                                    key={chatRoom.uuid + i}
                                                    chatRoom={chatRoom}
                                                    active={activeRoomId === chatRoom.uuid}
                                                />
                                            ))}
                                        </ul>
                                    </div>
                                </InfiniteScroll>
                            )}
                            {chatRooms.length === 0 && (
                                <div className="d-flex w-100 justify-content-center align-items-center px-3">
                                    <div className="length-zero">
                                        <h3>No recent chats!</h3>
                                    </div>
                                </div>
                            )}
                        </SimpleBar>
                    </div>
                    <div className="user-chat w-100 overflow-hidden" ref={chatViewRef}>
                        {children}
                    </div>
                </div>
            </Container>
        </div>
    );
};

export default ChatLayout;
